import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Cinematic hero — single hero image with slow Ken-Burns zoom.
const industries = [
  {
    label: "The House",
    place: "Next in Luxury — International Advisory",
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/59q68a8z_home%20page%20next%20in%20luxury.jpeg",
  },
];

const FRAME_MS = 999999;

function isVideo(src) {
  return /\.(mp4|webm|mov)(\?|$)/i.test(src);
}

export default function CinematicHero({ children }) {
  const [i, setI] = useState(0);
  const n = industries.length;

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % n), FRAME_MS);
    return () => clearInterval(t);
  }, [n]);

  return (
    <section
      data-testid="cinematic-hero"
      className="relative w-full h-screen overflow-hidden"
      style={{ background: "#0A0A08" }}
    >
      {/* Cinematic frames — horizontal slide + subtle zoom */}
      {industries.map((f, idx) => {
        const active = idx === i;
        const prev = idx === (i - 1 + n) % n;
        const state = active ? "active" : prev ? "exiting" : "waiting";
        const transform =
          state === "active"
            ? "translateX(0) scale(1)"
            : state === "exiting"
            ? "translateX(-8%) scale(1.04)"
            : "translateX(10%) scale(1.04)";
        const opacity = state === "active" ? 1 : 0;
        return (
          <div
            key={idx}
            className="absolute inset-0"
            style={{
              opacity,
              transform,
              transition:
                "opacity 1100ms cubic-bezier(0.19, 1, 0.22, 1), transform 1400ms cubic-bezier(0.19, 1, 0.22, 1)",
              zIndex: active ? 1 : 0,
              willChange: "transform, opacity",
            }}
          >
            {isVideo(f.src) ? (
              <video
                src={f.src}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover cinematic-frame"
                style={{
                  animation: active ? "kenburns 6s ease-out forwards" : "none",
                }}
              />
            ) : (
              <img
                src={f.src}
                alt={f.label}
                className="w-full h-full object-cover cinematic-frame"
                style={{
                  animation: active ? "kenburns 6s ease-out forwards" : "none",
                }}
              />
            )}
          </div>
        );
      })}

      {/* Duotone + vignette overlay */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 2,
          background:
            "radial-gradient(120% 100% at 50% 45%, rgba(0,0,0,0.10) 20%, rgba(0,0,0,0.38) 70%, rgba(0,0,0,0.70) 100%)",
        }}
      />
      {/* Warm ivory tone-map for luxury b&w feel + strong readability layer */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 3,
          background:
            "linear-gradient(180deg, rgba(20,20,15,0.30) 0%, rgba(20,20,15,0.18) 40%, rgba(20,20,15,0.55) 100%)",
          mixBlendMode: "multiply",
        }}
      />
      {/* Grain */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          zIndex: 4,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          mixBlendMode: "overlay",
        }}
      />

      {/* Foreground editorial layer */}
      <div
        className="relative z-10 h-full flex flex-col justify-between px-6 md:px-12 lg:px-16 pt-32 pb-16"
        style={{ color: "#F4EFE7", textShadow: "0 1px 20px rgba(0,0,0,0.35)" }}
      >
        {/* Center display */}
        <div className="max-w-[1400px] mx-auto w-full mt-24">
          {children}
        </div>

        {/* Bottom row — description + single CTA */}
        <div className="grid grid-cols-12 gap-6 items-end mt-12 hero-body" data-testid="hero-footer">
          <div className="col-span-12 md:col-span-6">
            <p
              className="font-serif-italic leading-snug max-w-md"
              style={{
                fontSize: "clamp(1.05rem, 1.3vw, 1.4rem)",
                opacity: 0.95,
                textShadow: "0 1px 10px rgba(0,0,0,0.5)",
              }}
            >
              Next in Luxury is a house of impact for luxury brands ready to
              go from excellent to exceptional.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 flex md:justify-end">
            <Link
              to="/private-inquiry"
              data-testid="home-hero-benext"
              className="statement-cta statement-cta--filled"
            >
              Be the Next One
            </Link>
          </div>
        </div>
      </div>

      {/* Silent scroll cue — bare hairline */}
      <div
        aria-hidden
        className="scroll-cue"
        data-testid="hero-scroll-cue"
        style={{ bottom: "24px" }}
      >
        <div className="line-wrap" style={{ height: "48px" }} />
      </div>
    </section>
  );
}
